import { AnimatePresence } from "framer-motion";
import { useState } from "react";

import { ClickableElement } from "@/components/ClickableElement";
import { DataMode } from "@/components/flow/shared/Deploy/documentation/types";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { AnalyticsFlowEvent } from "@/analytics/types";
import { useFlow } from "@/contexts/FlowContext";
import { ShareButton } from "@/components/flow/deploy/ShareButton";
import { useVisibilityThroughWorkspace } from "@/hooks/useVisibilityThroughWorkspace";
import { IntegrationCodeSnippetModal } from "@/components/flow/shared/Deploy/IntegrationCodeSnippetModal";
import { useDeployContext } from "@/contexts/DeployContext";
import { projectSelector } from "@rflocal/shared";
import { useAuth } from "@/contexts/AuthContext";
import { useSignInAndCreateWorkflow } from "@/hooks/useSignInAndCreateWorkflow";

const DataSelector = ({
    icon,
    title,
    description,
    onClick,
    isSelected
}: {
    icon: string;
    title: string;
    description: string;
    onClick: () => void | Promise<void> | Promise<void | null>;
    isSelected: boolean;
}) => {
    return (
        <ClickableElement
            icon={icon}
            title={title}
            description={description}
            onClick={onClick}
            isSelected={isSelected}
            hoverClassName="hover:bg-white hover:border-brand"
            iconClassName="text-brand"
            titleHoverClassName="group-hover:text-purboflow-600"
            descriptionHoverClassName="group-hover:text-purboflow-600"
        />
    );
};

export const IntegrateSideMenu = () => {
    const { task } = useFlow();
    const { track } = useAnalytics();
    const { workflowCredentials } = useDeployContext();
    const [isIntegrationModalOpen, setIsIntegrationModalOpen] = useState(false);
    const { user } = useAuth();
    const { signInAndCreateWorkflow } = useSignInAndCreateWorkflow();

    const visibility = useVisibilityThroughWorkspace();

    const handleOpenIntegrationModal = () => {
        setIsIntegrationModalOpen(true);
        track({
            event: AnalyticsFlowEvent.DATA_MODE_SELECTED,
            data: { task, dataMode: DataMode.IMAGE }
        });
    };

    return (
        <AnimatePresence mode="wait">
            <div className="content flex flex-col justify-between">
                <div className="flex flex-col gap-2">
                    <DataSelector
                        icon="far fa-code"
                        title="Integrate My Model"
                        description="Run your model calling our Serverless API."
                        onClick={handleOpenIntegrationModal}
                        isSelected={false}
                    />
                    <DataSelector
                        icon="far fa-network-wired"
                        title="Add Intelligence & Automation"
                        description="Add intelligence and smart automation to your model with ready-made or custom workflows."
                        onClick={() => {
                            if (!user) {
                                signInAndCreateWorkflow();
                                return;
                            }
                            track({
                                event: AnalyticsFlowEvent.OPEN_WORKFLOWS_BUTTON_CLICKED,
                                data: {
                                    workflowUrl: workflowCredentials.workflowUrl,
                                    workspaceUrl: workflowCredentials.workspaceUrl
                                }
                            });
                            window.open(
                                `${projectSelector.appUrl(false, true)}/${workflowCredentials.workspaceUrl}/workflows/edit/${workflowCredentials.workflowUrl}`,
                                "_blank"
                            );
                        }}
                        isSelected={false}
                    />
                </div>

                <IntegrationCodeSnippetModal
                    isOpen={isIntegrationModalOpen}
                    onClose={() => setIsIntegrationModalOpen(false)}
                />
            </div>
        </AnimatePresence>
    );
};
